import { useState, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import DOMPurify from 'dompurify';
import './Suppliers.css';

const API = import.meta.env.VITE_API_URL;

async function getToken(instance) {
  try {
    const accounts = instance.getAllAccounts();
    if (!accounts.length) return null;
    const resp = await instance.acquireTokenSilent({
      scopes: [`${import.meta.env.VITE_AZURE_CLIENT_ID}/.default`],
      account: accounts[0],
    });
    return resp.idToken;
  } catch {
    const accounts = instance.getAllAccounts();
    return accounts[0]?.idToken || null;
  }
}

export default function FkPage() {
  const { instance } = useMsal();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [blocked, setBlocked] = useState(false);

  // Detail state
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detailBlocked, setDetailBlocked] = useState(false);
  const [detailPath, setDetailPath] = useState(null);
  const [lang, setLang] = useState('nl'); // 'nl' original | 'en' translated
  const [detailEn, setDetailEn] = useState(null);
  const [translating, setTranslating] = useState(false);

  const handleSearch = useCallback(async (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    setError(null);
    setBlocked(false);
    setResults(null);
    setDetail(null);
    try {
      const token = await getToken(instance);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API}/api/market-intelligence/fk/search?q=${encodeURIComponent(q)}`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.blocked) {
        setBlocked(true);
      } else {
        setResults(json.results || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [instance, query]);

  const pollRef = useRef(null);
  const pollCount = useRef(0);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
    pollCount.current = 0;
  }, []);

  // Open a medicine → show the ORIGINAL Dutch immediately (no translation).
  const openDetail = useCallback(async (fkPath) => {
    setDetail(null); setDetailLoading(true); setDetailError(null); setDetailBlocked(false);
    setDetailPath(fkPath); setLang('nl'); setDetailEn(null); setTranslating(false); stopPolling();
    try {
      const token = await getToken(instance);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API}/api/market-intelligence/fk/detail-nl?path=${encodeURIComponent(fkPath)}`, { headers });
      const body = await res.json().catch(() => ({}));
      if (body.blocked) { setDetailBlocked(true); setDetailLoading(false); return; }
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setDetail(body); setDetailLoading(false);
    } catch (err) {
      setDetailError(err.message); setDetailLoading(false);
    }
  }, [instance, stopPolling]);

  // On demand: translate the open medicine to English (existing translate+poll flow).
  const translateToEnglish = useCallback(() => {
    if (detailEn) { setLang('en'); return; }
    if (!detailPath) return;
    setTranslating(true); stopPolling(); pollCount.current = 0;
    const fetchOnce = async () => {
      const token = await getToken(instance);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API}/api/market-intelligence/fk/detail?path=${encodeURIComponent(detailPath)}`, { headers });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || `HTTP ${res.status}`); }
      return res.json();
    };
    const poll = async () => {
      try {
        const data = await fetchOnce();
        if (data.status === 'ready') { setDetailEn(data); setLang('en'); setTranslating(false); stopPolling(); }
        else if (data.status === 'translating') {
          pollCount.current++;
          if (pollCount.current > 60) { setTranslating(false); stopPolling(); }
          else { pollRef.current = setTimeout(poll, 3000); }
        } else { setTranslating(false); }
      } catch { setTranslating(false); stopPolling(); }
    };
    poll();
  }, [detailEn, detailPath, instance, stopPolling]);

  const closeDetail = () => { setDetail(null); setDetailError(null); setDetailBlocked(false); setOpenSections({}); setDetailPath(null); setLang('nl'); setDetailEn(null); setTranslating(false); stopPolling(); };

  // Currently-shown version (original Dutch, or English translation on demand)
  const shown = (lang === 'en' && detailEn) ? detailEn : detail;
  const isEn = lang === 'en' && !!detailEn;

  // Accordion state: which section indices are open
  const [openSections, setOpenSections] = useState({});
  const toggleSection = (i) => setOpenSections(prev => ({ ...prev, [i]: !prev[i] }));
  const allOpen = shown?.sections ? shown.sections.every((_, i) => openSections[i]) : false;
  const toggleAll = () => {
    if (!shown?.sections) return;
    const next = {};
    const shouldOpen = !allOpen;
    shown.sections.forEach((_, i) => { next[i] = shouldOpen; });
    setOpenSections(next);
  };

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 22px', background: 'var(--surf)' }}>
      <h4 style={{ fontWeight: 700, marginBottom: 4, color: 'var(--t1)' }}>Farmacotherapeutisch Kompas (FK)</h4>
      <div style={{ marginBottom: 14, fontSize: 11.5, color: 'var(--t3)' }}>
        Search the Dutch pharmacotherapy reference for medicines and preparations.
      </div>

      {/* Search box */}
      <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, maxWidth: 520 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <input
            type="text"
            placeholder="Search medicine (e.g. paracetamol, metformine)..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            style={{
              width: '100%', padding: '8px 30px 8px 10px', fontSize: 13,
              border: '1px solid var(--teal)', borderRadius: 6,
              background: 'var(--card)', color: 'var(--t1)', outline: 'none', boxSizing: 'border-box',
            }}
          />
          {query && (
            <button
              type="button"
              onClick={() => { setQuery(''); setResults(null); setError(null); setBlocked(false); closeDetail(); }}
              style={{
                position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', color: 'var(--t3)',
                fontSize: 15, lineHeight: 1, padding: 0,
              }}
              title="Clear"
            >x</button>
          )}
        </div>
        <button
          type="submit"
          disabled={loading || !query.trim()}
          style={{
            padding: '8px 16px', fontSize: 13, fontWeight: 500,
            color: 'white', background: loading ? '#999' : 'var(--teal)',
            border: 'none', borderRadius: 6, cursor: loading ? 'wait' : 'pointer', whiteSpace: 'nowrap',
          }}
        >{loading ? 'Searching...' : 'Search'}</button>
      </form>

      {/* Search error */}
      {error && (
        <div style={{ padding: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, color: '#991b1b', fontSize: 13, marginBottom: 16 }}>
          Failed to search FK: {error}
        </div>
      )}

      {/* Blocked by FK (search) */}
      {blocked && (
        <div style={{ padding: 20, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, color: '#92400e', fontSize: 13, marginBottom: 16 }}>
          <strong>FK search is currently unavailable from the server.</strong><br />
          The Farmacotherapeutisch Kompas website requires a browser-based connection.{' '}
          <a href="https://www.farmacotherapeutischkompas.nl" target="_blank" rel="noopener noreferrer" style={{ color: '#1d4ed8' }}>Search directly on FK</a>.
        </div>
      )}

      {/* Search loading */}
      {loading && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>Searching FK...</div>
      )}

      {/* Detail view */}
      {(detailLoading || detail || detailError || detailBlocked) && (
        <div style={{ marginBottom: 16 }}>
          {/* Back button */}
          <button type="button" onClick={closeDetail} style={{ marginBottom: 10, padding: '5px 12px', fontSize: 12, color: 'var(--teal)', background: 'var(--card)', border: '1px solid var(--teal)', borderRadius: 6, cursor: 'pointer' }}>
            Back to results
          </button>

          {detailLoading && (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--t3)', fontSize: 13, background: 'var(--card)', borderRadius: 10, border: '1px solid #eef3f7' }}>
              <div style={{ fontWeight: 600 }}>Loading...</div>
            </div>
          )}

          {detailBlocked && (
            <div style={{ padding: 20, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, color: '#92400e', fontSize: 13 }}>
              <strong>FK detail page requires a headless browser.</strong><br />
              The server could not fetch the detail page directly. A headless-browser integration is planned.
            </div>
          )}

          {detailError && (
            <div style={{ padding: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, color: '#991b1b', fontSize: 13 }}>
              Failed to load detail: {detailError}
            </div>
          )}

          {detail && (
            <div style={{ background: 'var(--card)', borderRadius: 12, border: '1px solid #eef3f7', overflow: 'hidden' }}>
              <style>{`
.fk-section-body { font-size: 13px; color: var(--t1); line-height: 1.65; }
.fk-section-body ul { margin: 4px 0 4px 18px; padding: 0; }
.fk-section-body li { margin: 3px 0; }
.fk-section-body p { margin: 6px 0; }
.fk-section-body a { color: #1d4ed8; }
.fk-section-body table { border-collapse: collapse; margin: 8px 0; font-size: 12px; width: 100%; }
.fk-section-body td, .fk-section-body th { border: 1px solid #dee2e6; padding: 5px 8px; text-align: left; }
.fk-section-body th { background: #f8fafc; font-weight: 600; }

/* Preparation blocks (Samenstelling section) */
.fk-section-body section.recipe { margin: 10px 0; padding: 10px 12px; background: #f8fafc; border: 1px solid #eef3f7; border-radius: 8px; }
.fk-section-body section.recipe + section.recipe { margin-top: 8px; }
.fk-section-body .recipe h3, .fk-section-body .recipe .title { margin: 0 0 6px; font-size: 13px; font-weight: 700; color: var(--t1); display: flex; flex-wrap: wrap; align-items: baseline; gap: 6px; }
.fk-section-body .recipe .name { font-weight: 700; }
.fk-section-body .recipe .manfact { font-weight: 400; color: var(--t3); font-size: 12px; }
.fk-section-body .recipe .manfact::before { content: '— '; }
.fk-section-body .recipe .xgvs, .fk-section-body .recipe .otc, .fk-section-body .recipe .gvs {
  font-size: 10px; font-weight: 600; padding: 1px 5px; border-radius: 3px;
  background: #eef3f7; color: var(--t2); display: inline-block; vertical-align: middle; }

/* Definition lists (dosage form / strength / package) */
.fk-section-body dl.details { margin: 4px 0; display: grid; grid-template-columns: auto 1fr; gap: 2px 10px; font-size: 12.5px; }
.fk-section-body dl.details dt { color: var(--t3); font-weight: 500; white-space: nowrap; }
.fk-section-body dl.details dd { margin: 0; color: var(--t1); }
.fk-section-body .doses { margin: 4px 0; }
.fk-section-body .doses + .doses { margin-top: 6px; padding-top: 6px; border-top: 1px dashed #eef3f7; }
.fk-section-body .note { font-size: 12px; color: var(--t3); margin: 4px 0 0; font-style: italic; }

/* Sub-headings inside sections (e.g. dosage indication headings) */
.fk-section-body h3 { font-size: 13px; font-weight: 700; margin: 14px 0 4px; color: var(--t1); }
.fk-section-body h3.collapsible-closed, .fk-section-body h3.collapsible-open { cursor: default; }

/* Generic bold/strong */
.fk-section-body strong, .fk-section-body b { font-weight: 600; }
              `}</style>
              {/* Caveat banner */}
              <div style={{ padding: '10px 16px', background: '#fffbeb', borderBottom: '1px solid #fde68a', fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
                {isEn
                  ? <>Machine-translated from the Dutch Farmacotherapeutisch Kompas. <strong>Verify dosing and clinical details against the source.</strong>{' '}</>
                  : <>Original Dutch from the Farmacotherapeutisch Kompas.{' '}</>}
                <a href={detail.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1d4ed8', fontWeight: 600 }}>Open on FK</a>
              </div>

              {/* Title + language toggle + Expand/Collapse */}
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #eef3f7', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <h4 style={{ margin: 0, fontWeight: 700, color: 'var(--t1)', fontSize: 16 }}>{shown.title_en || shown.title}</h4>
                <div style={{ display: 'flex', gap: 8 }}>
                  {isEn
                    ? <button type="button" onClick={() => setLang('nl')} style={{ fontSize: 12, color: 'var(--teal)', background: 'none', border: '1px solid var(--teal)', borderRadius: 5, padding: '3px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>Show original (Dutch)</button>
                    : <button type="button" onClick={translateToEnglish} disabled={translating} style={{ fontSize: 12, color: '#fff', background: translating ? '#9cc6d6' : 'var(--teal)', border: 'none', borderRadius: 5, padding: '4px 11px', cursor: translating ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>{translating ? 'Translating…' : 'Translate to English'}</button>}
                  <button type="button" onClick={toggleAll} style={{ fontSize: 12, color: 'var(--teal)', background: 'none', border: '1px solid var(--teal)', borderRadius: 5, padding: '3px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    {allOpen ? 'Collapse all' : 'Expand all'}
                  </button>
                </div>
              </div>

              {/* Accordion sections */}
              <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {shown.sections && shown.sections.map((s, i) => {
                  const isOpen = !!openSections[i];
                  const heading = s.heading_en || s.heading || `Section ${i + 1}`;
                  const bodyHtml = s.body_html_en || s.body || '';
                  return (
                    <div key={i} style={{ borderBottom: '1px solid #eef3f7' }}>
                      <button
                        type="button"
                        onClick={() => toggleSection(i)}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '10px 16px', background: isOpen ? '#f0fdf4' : 'transparent',
                          border: 'none', cursor: 'pointer', textAlign: 'left',
                          transition: 'background 0.15s',
                        }}
                      >
                        <span style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--teal)' }}>{heading}</span>
                        <span style={{ fontSize: 11, color: 'var(--t3)', flexShrink: 0, marginLeft: 8 }}>{isOpen ? '\u25B2' : '\u25BC'}</span>
                      </button>
                      {isOpen && (
                        <div style={{ padding: '8px 16px 14px' }}>
                          {s.note && <div style={{ fontSize: 11, color: '#92400e', marginBottom: 6, fontStyle: 'italic' }}>{s.note}</div>}
                          <div
                            className="fk-section-body"
                            style={{ fontSize: 13, color: 'var(--t1)', lineHeight: 1.65 }}
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(bodyHtml, { ADD_ATTR: ['target'] }) }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Search results (hidden when detail is open) */}
      {!loading && !detailLoading && !detail && !detailError && !detailBlocked && results !== null && (
        <div className="sup-table-wrap">
          {results.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>
              No medicines found for this search term.
            </div>
          ) : (
            <>
              <div style={{ padding: '8px 14px', fontSize: 12, color: 'var(--t3)', borderBottom: '1px solid #eef3f7' }}>
                {results.length} result{results.length !== 1 ? 's' : ''} found
              </div>
              <div className="sup-table-scroll" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '65vh' }}>
                <table className="sup-table" style={{ tableLayout: 'auto' }}>
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>#</th>
                      <th>Medicine</th>
                      <th style={{ width: 100 }}>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r, i) => (
                      <tr key={i}>
                        <td style={{ whiteSpace: 'nowrap', color: 'var(--t3)' }}>{i + 1}</td>
                        <td>
                          <button
                            type="button"
                            onClick={() => openDetail(r.fkPath)}
                            style={{ background: 'none', border: 'none', padding: 0, color: '#1d4ed8', cursor: 'pointer', textDecoration: 'none', fontSize: 'inherit', fontFamily: 'inherit', textAlign: 'left', fontWeight: 500 }}
                            onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                            onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                          >{r.name}</button>
                        </td>
                        <td>
                          <a
                            href={r.fkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: 12, color: 'var(--teal)', textDecoration: 'none' }}
                            title="Open Dutch original on FK"
                          >FK</a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
